import { fetchWithAuth } from "@/lib/auth";

export interface PromptVersionSummary {
  version: number;
  change_note: string | null;
  created_at: string;
  created_by: string | null;
}

export interface PromptSummary {
  key: string;
  workflow: string;
  label: string;
  description: string;
  is_customized: boolean;
  version: number;
  response_format: string | null;
  updated_at: string;
  updated_by: string | null;
  code_default_changed: boolean;
}

export interface PromptDetail extends PromptSummary {
  system_template: string;
  user_template: string | null;
  temperature: number;
  max_tokens: number | null;
  required_placeholders: string[];
  default_system_template: string;
  default_user_template: string | null;
  history: PromptVersionSummary[];
}

export interface PromptUpdate {
  system_template: string;
  user_template: string | null;
  temperature: number;
  max_tokens: number | null;
  version: number;
  change_note: string;
}

export interface PromptRevertRequest {
  version: number;
  change_note?: string;
}

export async function getPromptConsoleAccess(): Promise<{ can_view: boolean, is_prompt_admin: boolean, can_edit: boolean }> {
  const res = await fetchWithAuth("/api/v1/prompts-console/check-access");
  if (!res.ok) throw new Error("Failed to check access");
  return res.json();
}

export async function getPrompts(): Promise<Record<string, PromptSummary[]>> {
  const res = await fetchWithAuth("/api/v1/prompts-console");
  if (!res.ok) throw new Error("Failed to load prompts");
  return res.json();
}

export async function getPromptDetail(key: string): Promise<PromptDetail> {
  const res = await fetchWithAuth(`/api/v1/prompts-console/${encodeURIComponent(key)}`);
  if (!res.ok) throw new Error("Failed to load prompt detail");
  return res.json();
}

export async function updatePrompt(key: string, data: PromptUpdate): Promise<PromptDetail> {
  const res = await fetchWithAuth(`/api/v1/prompts-console/${encodeURIComponent(key)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    if (res.status === 409) {
      throw new Error(errorData?.detail || "Conflict: this prompt was edited by someone else. Please reload.");
    }
    throw new Error(errorData?.detail || "Failed to save prompt");
  }
  
  return res.json();
}

export async function revertPrompt(key: string, data: PromptRevertRequest): Promise<PromptDetail> {
  const res = await fetchWithAuth(`/api/v1/prompts-console/${encodeURIComponent(key)}/revert`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new Error(errorData?.detail || "Failed to revert prompt");
  }
  return res.json();
}

export async function resetPrompt(key: string, version: number): Promise<PromptDetail> {
  const res = await fetchWithAuth(`/api/v1/prompts-console/${encodeURIComponent(key)}/reset`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ version }),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => null);
    throw new Error(errorData?.detail || "Failed to reset prompt");
  }
  return res.json();
}
